import { isChargingStatistics, chargingParameter } from './charging-statistics-model'
import { cloneConfig, createLegacySavePayload } from '../schema/legacy-adapter'
import { createPageSavePayload } from '../schema/page-project'
import { isDeviceChart } from './device-chart-data'
import { isDeviceValue } from './device-value-data'
import { locationParameter } from './device-map-model'

const prefix = 'visual-preview:'
const lifetime = 15 * 60 * 1000

export function createPreviewSnapshot (record, metadata, config, nodes, storage = sessionStorage, now = Date.now()) {
  const copy = cloneConfig(metadata || {})
  if (copy.visual) delete copy.visual.password
  const project = copy.pageProject
  const home = project && project.pages.find(page => page.id === project.homePageId)
  // 多页快照由调用方同步当前页；兼容单页字段始终映射首页，与正式保存一致。
  const payload = project
    ? createPageSavePayload(record, copy, project, project.homePageId, home && home.config, home && home.nodes)
    : createLegacySavePayload(record, copy, config, nodes)
  const id = `${now.toString(36)}-${Math.random().toString(36).slice(2)}`
  const snapshot = { projectId: record.id, expiresAt: now + lifetime, record: { ...record, ...payload } }
  // 仅清理本模块过期快照，不触碰登录或其他应用存储。
  const keys = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && key.startsWith(prefix)) keys.push(key)
  }
  keys.forEach(key => {
    try {
      if (JSON.parse(storage.getItem(key)).expiresAt <= now) storage.removeItem(key)
    } catch (error) { storage.removeItem(key) }
  })
  storage.setItem(prefix + id, JSON.stringify(snapshot))
  return id
}

export function readPreviewSnapshot (id, projectId, storage = sessionStorage, now = Date.now()) {
  let snapshot
  try { snapshot = JSON.parse(storage.getItem(prefix + id)) } catch (error) { /* 下方统一给出恢复提示 */ }
  if (!snapshot || !snapshot.record || snapshot.projectId !== projectId || !Number.isFinite(snapshot.expiresAt) || snapshot.expiresAt <= now) {
    throw new Error('预览内容不存在或已过期，请回到编辑器重新点击预览')
  }
  return cloneConfig(snapshot.record)
}

export function previewNodes (nodes, { liveDeviceData = false } = {}) {
  const copy = cloneConfig(nodes)
  const visit = list => list.forEach(node => {
    if (node.children) visit(node.children)
    else {
      // 普通项目预览可显式接续原生设备数据；其他预览仍保持只读 HTTP 白名单。
      // 此处不开放任意动态接口、自定义 WS 或处理脚本。
      const mode = node.parameter && node.parameter.configDataType !== undefined ? node.parameter.configDataType : 0
      const historyChart = isDeviceChart(node) && [0, 1].includes(Number(mode))
      const liveChart = liveDeviceData === true && isDeviceChart(node) && Number(mode) === 2
      const latestValue = isDeviceValue(node)
      const nativeRead = Number(node.dataType) === 2 && node.component && ['weekSchedule'].includes(node.component.name) && node.component.name === node.component.prop
      const deviceMap = liveDeviceData === true && Number(node.dataType) === 5 && node.component &&
        ['map', 'Dmap'].includes(node.component.name) && node.component.name === node.component.prop
      const charging = liveDeviceData === true && isChargingStatistics(node)
      if (charging) node.parameter = chargingParameter(node.parameter, node.component.name)
      node.dataType = charging ? 6 : deviceMap ? 5 : historyChart || liveChart || latestValue || nativeRead ? 2 : 0
      node.time = 0
      if (deviceMap) {
        node.parameter = locationParameter(node.parameter)
        node.data = []
        for (const key of ['url', 'dataMethod', 'dataQuery']) delete node[key]
      }
      if (historyChart || liveChart) {
        node.parameter = { ...node.parameter, refreshInterval: 0 }
        if (liveDeviceData !== true) node.parameter.liveFollow = false
      }
      for (const key of ['dataScript', 'dataFormatter', 'clickFormatter', 'echartFormatter', 'labelFormatter', 'formatter']) node[key] = ''
    }
  })
  visit(copy)
  return copy
}
